<template lang="pug">
.event-preview-component
  .preview-wrapper
    b-badge.need-reply-badge(v-if="needReply" pill) 要申込
    .flex-preview(v-bind:style="_previewStyle")
      .title.mb-2
        .text-break {{ title }}
      .date.d-flex.flex-row.ml-2.mb-2
        .name 日付
        .text(v-show="date") {{ _getDate }}（{{ _getWeek }}）
      .time.d-flex.flex-row.ml-2.mb-2
        .name 時間
        .text(v-show="timeFrom") {{ _getTimeFrom }} - {{ _getTimeTo }}
      .hold-type.d-flex.flex-row.ml-2.mb-2
        .name 開催
        .text {{ _getHoldTypeName }}
      .place.d-flex.flex-row.ml-2.mb-2
        .name 場所
        .text.text-break {{ place }}
      hr
      .contents.pre-line.text-break {{ description }}
      .temp-file.d-flex.flex-row.align-items-center.ml-2.mb-2(v-for="file in files" :key="file.name")
        .file-name.flex-fill.mr-2 {{ file.name }}
        b-button.confirm-btn(variant="outline-primary") 確認
      b-button.reply-btn.pl-2.pr-2.mb-2(v-if="needReply") 申込みに進む
      .dead-line.d-flex.flex-row.align-items-center.mb-2(v-if="needReply")
        b-badge.dead-line-badge(v-if="needReply") 締切
        .dead-line-text.ml-2 {{ _getReplyDeadline }}
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import eventDefaultImage from "@/assets/event-default.jpg";
import { EventHoldType } from "@/commons/services/Events";
import { ExtDraggableFile } from "@/pc/components/ExtDraggableFiles.vue";

/**
 * イベントプレビュー
 */
@Component
export default class EventPreview extends Vue {
  /** 日付 */
  @Prop({ type: Number })
  date!: number;

  /** 開始時刻 */
  @Prop({ type: String })
  timeFrom!: string;

  /** 終了時刻 */
  @Prop({ type: String })
  timeTo!: string;

  /** 場所 */
  @Prop({ type: String })
  place!: string;

  /** タイトル */
  @Prop({ type: String })
  title!: string;

  /** 開催 */
  @Prop({ type: Number || null })
  holdType!: number;

  /** 参加申込み */
  @Prop({ type: Boolean || null })
  needReply!: boolean;

  /** 参加申込期日 */
  @Prop({ type: Number })
  replyDeadline!: number;

  /** 内容 */
  @Prop({ type: String })
  description!: string;

  /** 実際に登録するファイル */
  @Prop({ type: String })
  files!: Array<ExtDraggableFile>;

  /** 画像URL */
  @Prop()
  imageUrl!: string;

  /**
   * vue lifecycle created
   */
  public created(): void {
    return;
  }

  get _previewStyle(): any {
    let style = {};

    if (this.imageUrl) {
      style = { background: `url(${this.imageUrl}) 0% 0%/453px 300px no-repeat` };
    } else {
      style = { background: `url(${eventDefaultImage}) 0% 0%/453px 300px no-repeat` };
    }

    return style;
  }

  get _getDate(): string {
    let date: string = "";
    if (this.date) {
      date = this.$dateUtil.convertNumberToString(this.date, "yyyy年mm月dd日", false);
    }

    return date;
  }

  get _getTimeFrom(): string {
    let timeFrom: string = "";
    if (this.timeFrom) {
      timeFrom = this.timeFrom.slice(0, 5);
    }

    return timeFrom;
  }

  get _getTimeTo(): string {
    let timeTo: string = "";
    if (this.timeTo) {
      timeTo = this.timeTo.slice(0, 5);
    }

    return timeTo;
  }

  /** 曜日文字列 */
  get _getWeek(): string {
    let weekName: string = "";
    if (this.date) {
      // 日付はyyyy年mm月dd日の書式になっているので、数値に変換した後にDate型に変換する
      let date: string = this.$dateUtil.convertNumberToString(this.date, "yyyy/mm/dd hh:MM:ss");
      const eventDate: Date = new Date(date);
      weekName = this.$dateUtil.getWeekName(eventDate, "ja");
    }
    return weekName;
  }

  get _getHoldTypeName(): string {
    if (this.holdType == null) {
      return "";
    }

    switch (this.holdType) {
      case EventHoldType.LOCAL_ONLY: {
        return "現地";
      }
      case EventHoldType.ONLINE_ONLY: {
        return "オンライン";
      }
      case EventHoldType.LOCAL_AND_ONLINE: {
        return "現地とオンライン";
      }
      default: {
        return "";
      }
    }
  }

  get _getReplyDeadline(): string {
    if (this.needReply == true && this.replyDeadline != null) {
      const replyDeadlineDate: string = this.$dateUtil.convertNumberToString(this.replyDeadline, "mm/dd", false);

      const replyDeadline: string = this.$dateUtil.convertNumberToString(this.replyDeadline, "yyyy/mm/dd hh:MM:ss");
      const _replyDeadline: Date = new Date(replyDeadline);
      const weekName: string = this.$dateUtil.getWeekName(_replyDeadline, "ja");

      return `${replyDeadlineDate}(${weekName})`;
    } else {
      return "参加申込み受付無し";
    }
  }
}
</script>

<style lang="scss" scoped>
.event-preview-component {
  // .flex-previewにborderを指定すると背景画像がズレるので、枠線用の段落でラップする
  .preview-wrapper {
    border: solid 2px rgba(0, 0, 0, 0.5);
    border-radius: 20px;
    width: 457px;
    display: block;
    position: relative;

    // 要申込のバッジ
    .need-reply-badge {
      position: absolute;
      top: 10px;
      left: 0px;
      z-index: 1;

      padding-top: 0.4rem;
      padding-bottom: 0.4rem;
      padding-left: 0.8rem;
      padding-right: 0.8rem;

      background-color: #fff00e;
      color: #034656;
      font-size: 16px !important;
    }

    .flex-preview {
      padding: 310px 16px 16px 16px;
      width: 453px;
      border-radius: 20px;
      background-color: rgba(255, 255, 255, 1);

      .sub-title {
        margin: 8px 0 0 8px;

        span {
          font-size: 14px !important;
        }
      }

      .title {
        margin: 8px 0 0 8px;
        font-weight: bold;
        font-size: 20px;
      }
      .date,
      .time,
      .place,
      .hold-type,
      .reply-deadline {
        & > .name {
          display: inline-block;
          width: 40px;
          min-width: 40px;
          font-weight: bold;
          color: #646464;
          font-size: 14px;
        }
        & > .text {
          font-size: 16px;
          color: #333333;
          font-weight: bold;
        }
        word-wrap: break-word;
      }
      .contents {
        padding: 0 8px 8px 8px;
        font-size: 16px;
        word-wrap: break-word;
      }
    }

    .temp-file {
      .file-name {
        color: #00a3be !important;
        font-weight: bold;
        font-size: 16px;
        text-decoration: underline;
      }
      .confirm-btn {
        height: 36px !important;
        min-width: 50px;
        background-color: #fff !important;
        color: #00a3be !important;
        border-radius: 0.5rem !important;
        font-size: 14px !important;
      }
    }

    .reply-btn {
      width: 100%;
      border-radius: 0.5rem !important;
      background-color: #00a3be !important;
      color: #fff !important;
      font-size: 16px !important;
      font-weight: bold;
    }

    .dead-line {
      .dead-line-badge {
        margin: 0 !important;
        padding-top: 0.4rem;
        padding-bottom: 0.4rem;
        padding-left: 0.4rem;
        padding-right: 0.4rem;
        border-radius: 0.5rem !important;
        background-color: #fec725 !important;
        color: #4d4d4d !important;
        font-size: 14px !important;
      }
      .dead-line-text {
        color: #646464;
      }
    }
  }
}
</style>
